import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Play, Loader2, PhoneCall, UserCheck, CalendarCheck, Clock, MessageSquare } from "lucide-react";

const mockResults = {
  callerName: "Sarah Johnson",
  callerPhone: "+44 7911 234567",
  serviceRequested: "Dental Check-up",
  callDuration: "1m 42s",
  leadsToday: 32,
  appointmentsToday: 18,
  conversation: [
    { role: "ai", text: "Hello! Thank you for calling Sunrise Dental. How can I help you today?" },
    { role: "caller", text: "Hi, I'd like to book a dental check-up please." },
    { role: "ai", text: "Of course! Can I get your name please?" },
    { role: "caller", text: "Sarah Johnson." },
    { role: "ai", text: "Thank you, Sarah. I have availability on Tuesday at 1:00 PM or Wednesday at 10:00 AM. Which works better for you?" },
    { role: "caller", text: "Tuesday at 1 works great." },
    { role: "ai", text: "You're all set! I've booked you for Tuesday, January 14th at 1:00 PM. You'll receive a confirmation text shortly. Is there anything else I can help with?" },
  ],
};

export default function ProductPreviewSection() {
  const [state, setState] = useState("idle");

  const handleAnalyse = () => {
    setState("loading");
    setTimeout(() => setState("results"), 2200);
  };

  const handleReset = () => setState("idle");

  return (
    <section id="product-preview" data-testid="product-preview-section" className="py-10 md:py-[85px] bg-white">
      <div className="section-container">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-medium uppercase tracking-widest text-[#3B82F6] mb-4">
            Product Preview
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] tracking-tight text-balance">
            See the AI Receptionist{" "}
            <span className="text-[#3B82F6]">in Action</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Watch a simulated call where the AI answers the customer, captures key details, checks availability, and books an appointment automatically.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-[#0F172A] rounded-t-xl p-3 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="bg-white/10 rounded-md px-4 py-1">
                <span className="font-mono text-xs text-slate-400">app.aicallfront.com/calls</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-t-0 border-slate-200 rounded-b-xl p-6 md:p-8 min-h-[400px]" data-testid="product-preview-container">
            {state === "idle" && (
              <div className="flex flex-col items-center justify-center py-16 animate-fade-in">
                <div className="w-20 h-20 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
                  <PhoneCall className="w-10 h-10 text-[#3B82F6]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#0F172A] mb-2">
                  Sample Call Simulation
                </h3>
                <p className="text-sm text-slate-500 mb-6 text-center max-w-md">
                  Click below to simulate an incoming call to "Sunrise Dental" and see how the AI handles it end-to-end.
                </p>
                <Button
                  onClick={handleAnalyse}
                  className="bg-[#3B82F6] text-white h-12 px-8 rounded-lg font-medium hover:bg-blue-600 transition-all shadow-sm hover:shadow-md group"
                  data-testid="analyze-sample-button"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Simulate Incoming Call
                </Button>
              </div>
            )}

            {state === "loading" && (
              <div className="flex flex-col items-center justify-center py-16 animate-fade-in">
                <Loader2 className="w-12 h-12 text-[#3B82F6] animate-spin mb-6" />
                <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-2">
                  Incoming Call...
                </h3>
                <p className="text-sm text-slate-500 mb-4">AI receptionist answering and processing the call</p>
                <div className="w-64">
                  <Progress value={66} className="h-2" />
                </div>
              </div>
            )}

            {state === "results" && (
              <div className="animate-fade-in space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <Badge className="bg-green-50 text-green-700 border-green-200 mb-2">Call Completed</Badge>
                    <h3 className="font-heading text-lg font-semibold text-[#0F172A]">Sunrise Dental — Incoming Call</h3>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-preview-button">
                    Run Again
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-xl border border-slate-100 p-4" data-testid="compliance-score-card">
                    <div className="flex items-center gap-2 mb-2">
                      <UserCheck className="w-4 h-4 text-[#3B82F6]" strokeWidth={1.5} />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Lead Captured</span>
                    </div>
                    <span className="font-heading font-bold text-lg text-[#0F172A]">{mockResults.callerName}</span>
                    <p className="text-xs text-slate-400 mt-0.5">{mockResults.callerPhone}</p>
                  </div>

                  <div className="bg-slate-50 rounded-xl border border-slate-100 p-4" data-testid="risk-flags-card">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-500" strokeWidth={1.5} />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Call Duration</span>
                    </div>
                    <span className="font-heading font-bold text-lg text-[#0F172A]">{mockResults.callDuration}</span>
                    <p className="text-xs text-slate-400 mt-0.5">Fully automated</p>
                  </div>

                  <div className="bg-green-50 rounded-xl border border-green-100 p-4" data-testid="bid-recommendation-card">
                    <div className="flex items-center gap-2 mb-2">
                      <CalendarCheck className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Result</span>
                    </div>
                    <span className="font-heading font-bold text-lg text-green-600">Appointment Booked</span>
                    <p className="text-xs text-slate-400 mt-0.5">Tue 14 Jan, 1:00 PM</p>
                  </div>
                </div>

                {/* Conversation */}
                <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                  <h4 className="font-heading font-semibold text-sm text-[#0F172A] mb-3">Call Transcript</h4>
                  <div className="space-y-2 max-h-[240px] overflow-y-auto">
                    {mockResults.conversation.map((msg, i) => (
                      <div key={i} className={`flex ${msg.role === "ai" ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 ${msg.role === "ai" ? "bg-blue-50 border border-blue-100" : "bg-white border border-slate-200"}`}>
                          <span className="text-[10px] font-medium text-slate-400 block mb-0.5">{msg.role === "ai" ? "AI Receptionist" : "Caller"}</span>
                          <p className="text-xs text-slate-700">{msg.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-[#3B82F6]" />
                    <span className="text-xs font-medium text-[#0F172A]">Staff Summary</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">New patient Sarah Johnson booked dental check-up for Tue 14 Jan at 1:00 PM. Has AXA insurance (Policy #4421). Confirmation sent. No follow-up needed.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
